<template>
  <v-container
    fluid
    class="ac2-page">
    <v-row>
      <v-col
        cols="12"
        :md="selectItem ? '8' : '12'">
        <v-card
          class="rounded-lg table-card">
          <v-data-table
            :headers="header"
            :items="items"
            :options.sync="options"
            :items-per-page="options.itemsPerPage"
            :server-items-length="total"
            :loading="loading"
            hide-default-footer
            class="w-full"
            @update:options="onOptionsUpdate"
          >
            <template v-slot:top>
              <div class="d-flex align-center flex-wrap px-4 pt-4 pb-2">
                <h6 class="text-h6 mb-0">
                  AC2 Request Logs
                </h6>
                <v-spacer />
                <v-autocomplete
                  v-model.trim="description"
                  :items="allDescription"
                  class="ml-4 flex-grow-0 search-text-field"
                  style="max-width: 260px;"
                  label="Description"
                  outlined
                  dense
                  hide-details
                  @change="getData(true)" />
                <search-box
                  v-model="requestId"
                  placeholder="Request ID"
                  class="ml-3 flex-grow-0"
                  style="max-width: 260px;"
                  @on-search="getData(true)" />
                <v-btn
                  icon
                  class="ml-2"
                  @click="getData()">
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </div>
              <v-divider />
            </template>

            <template #[`item.createdAt`]="{ item }">
              {{ item.createdAt | dateTimeFormat() }}
            </template>
            <template #[`item.method`]="{ item }">
              <div
                class="text-bold method"
                :style="getMethodStyle(item.method)">
                {{ item.method.toUpperCase() }}
              </div>
            </template>
            <template #[`item.responseTime`]="{ item }">
              {{ item.responseTime ? `${item.responseTime}ms` : '-' }}
            </template>
            <template #[`item.description`]="{ item }">
              <span
                class="text-bold"
                :style="getStatusStyle(item.httpStatus)">{{ item.httpStatus }}</span> {{ item.description }}
            </template>
            <template #[`item.option`]="{ item }">
              <v-btn
                v-if="!selectItem || selectItem.id !== item.id"
                link
                icon
                x-small
                @click="showItemDetail(item)">
                <v-icon>
                  mdi-magnify
                </v-icon>
              </v-btn>
            </template>
          </v-data-table>
          <v-divider />
          <div class="d-flex align-center flex-wrap px-4 py-3">
            <span class="text-caption grey--text">
              Total {{ total }} items
            </span>
            <v-spacer />
            <v-pagination
              v-model="options.page"
              :length="pageCount"
              :total-visible="5"
              color="secondary"
              @input="getData()" />
          </div>
        </v-card>
      </v-col>
      <v-col
        v-if="selectItem"
        cols="12"
        md="4">
        <v-card class="rounded-lg detail-card pa-4">
          <h3 class="text-subtitle-1 font-weight-bold mb-0">
            {{ selectItem.description }}
          </h3>
          <p class="mb-0 mt-2">
            <span
              :style="getStatusStyle(selectItem.httpStatus)"
              class="text-bold">{{ selectItem.httpStatus }}</span> {{ selectItem.statusText }}
          </p>
          <p class="mb-0">
            <b>Date </b>{{ selectItem.createdAt | dateTimeFormat() }}
          </p>
          <p
            v-if="selectItem.note"
            class="mb-0">
            <b>Note </b> {{ selectItem.note }}
          </p>
          <div class="mt-4">
            <a @click="scrollTo('Config')">#Config</a>
            <a
              class="ml-4"
              @click="scrollTo('Body')">#Body</a>
            <a
              class="ml-4"
              @click="scrollTo('Response')">#Response</a>
          </div>
          <div class="json-box mt-2">
            <json-viewer
              :value="{ method: selectItem.method, baseUrl: selectItem.baseUrl }"
              :expand-depth="1"
              copyable />
          </div>
          <p
            ref="Config"
            class="mt-4 mb-1 font-weight-medium">
            Config
          </p>
          <div class="json-box">
            <json-viewer
              :value="selectItem.config"
              :expand-depth="1"
              copyable />
          </div>
          <p
            ref="Body"
            class="mt-4 mb-1 font-weight-medium">
            Body
          </p>
          <div class="json-box">
            <json-viewer
              :value="selectItem.body || {}"
              :expand-depth="5"
              copyable
              sort />
          </div>
          <p
            ref="Response"
            class="mt-4 mb-1 font-weight-medium">
            Response
          </p>
          <div class="json-box">
            <json-viewer
              :value="selectItem.result"
              :expand-depth="5"
              copyable
              sort />
          </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import SearchBox from '@/components/SearchBox.vue'
import Ac2Provider from '@/resources/Ac2Provider'

const Ac2Service = new Ac2Provider()

export default {
  components: {
    SearchBox
  },
  data () {
    return {
      loading: false,
      selectItem: null,
      showDetail: false,
      total: 0,
      sortSnapshot: null,
      description: 'All',
      requestId: '',
      allDescription: [
      'All',
      'Generate access token',
      'Create or update item',
      'Disable item',
      'Create or update partner',
      'Disable partner',
      'Create or update PO',
      'Close PO',
      'Create receipt plan (GRI)',
      'Create receipt plan (ALI)',
      'Create receipt plan (TFI)',
      'Create receipt plan (ASI)',
      'Create receipt plan (SPI)',
      'Create receipt plan (SCI)',
      'Cancel receipt',
      'Create outbound order (TFO)',
      'Create outbound order (ALO)',
      'Create outbound order (ADJ)',
      'Create outbound order (SPO)',
      'Create outbound order (UNGRI)',
      'Create outbound order (E-COMM)',
      'Cancel outbound order'
    ],
      options: {
        page: 1,
        itemsPerPage: 15,
        sortBy: ['createdAt'],
        sortDesc: [true]
      },
      header: [
        {
          text: 'Date',
          value: 'createdAt',
          sortable: false
        },
        {
          text: 'Method',
          value: 'method',
          sortable: false
        },
        {
          text: 'Response Time',
          value: 'responseTime',
          align: 'center',
          sortable: false
        },
        {
          text: 'Description',
          value: 'description',
          sortable: false
        },
        {
          text: 'Options',
          value: 'option',
          align: 'center'
        }
      ],
      items: []
    }
  },
  computed: {
    pageCount () {
      return Math.ceil(this.total / this.options.itemsPerPage) || 1
    }
  },
  created () {
    this.getData(true)
  },
  methods: {
    onOptionsUpdate () {
      // Paging is handled by the separate v-pagination, so only refetch
      // when the sort changes. Skip the initial mount emit (created() loads).
      const sortKey = `${this.options.sortBy.join(',')}|${this.options.sortDesc.join(',')}`
      if (this.sortSnapshot === null) {
        this.sortSnapshot = sortKey
        return
      }
      if (sortKey !== this.sortSnapshot) {
        this.sortSnapshot = sortKey
        this.getData(true)
      }
    },
    showItemDetail (item) {
      this.selectItem = item
      this.showDetail = true
    },
    scrollTo (tag) {
      const el = this.$refs[tag]
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' })
      }
    },
    getMethodStyle (method) {
      switch (method.toUpperCase()) {
        case 'GET':
          return 'color: #3b9c28; background-color: #eef6e9;'
        case 'POST':
          return 'color: #e29f0d; background-color: #fff7ea;'
        case 'PUT':
          return 'color: #379afe; background-color: #edf3fe;'
        case 'DELETE':
          return 'color: #e93e4c; background-color: #ffecec;'
        default:
          return 'color: #18171d; background-color: #f8f8f8;'
      }
    },
    getStatusStyle (status) {
      if (+status >= 200 && +status < 300) {
        return 'color: #4CAF50;'
      }
      if (+status < 600) {
        return 'color: #F44336'
      }

      return ''
    },
    async getData (clear = false) {
      if (clear) {
        this.options.page = 1
      }
      try {
        this.loading = true
        const { data: { results, totalDocs } } = await Ac2Service.getRequestLogs(
          {
            page: this.options.page,
            limit: this.options.itemsPerPage,
            sortBy: this.options.sortBy[0],
            sortOrder: this.options.sortDesc[0] ? 'desc' : 'asc',
            description: this.description,
            requestId: this.requestId
          }
        )

        this.items = results
        this.total = totalDocs
        if (clear && results.length !== 0) {
          this.selectItem = this.items[0]
        }
      } catch (error) {
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `Error ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
<style scoped>
.table-card,
.detail-card {
  height: calc(100vh - 96px);
  overflow-y: auto;
}
.json-box {
  border: thin solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  overflow: hidden;
}
.theme--dark .json-box {
  border-color: rgba(255, 255, 255, 0.12);
}
.method {
  padding: 4px;
  border-radius: 6px;
  width: 60px;
  text-align: center;
}
</style>
